// ./src/hooks/useDisplaySchedule.ts
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  DisplayScheduleData,
  DisplayScheduleService,
  DisplayScheduleFilter,
} from "@/utils/api";

import { getScheduleDate, forceReloadToLatestVersion } from "@/utils";
import { useNetwork, useCache, useSignalRContext } from "@/contexts";
import { parseThemeConfig } from "@/components/features";
import { parseLayoutConfig } from "@/components/features/DisplayLayouts/LayoutFormModal/layoutConfigUtils";

export interface UseDisplayScheduleOptions {
  /** Filter object containing all schedule criteria */
  filter?: Partial<DisplayScheduleFilter>;

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Auto-refresh interval in milliseconds */
  refreshInterval?: number;
}

export interface UseDisplayScheduleReturn {
  // Data state
  scheduleData: DisplayScheduleData[];
  loading: boolean;
  isEmpty: boolean;

  // Filter state
  filter: DisplayScheduleFilter;

  // Data operations
  loadSchedule: (filter: DisplayScheduleFilter) => Promise<void>;
  refreshSchedule: () => Promise<void>;

  // Filter operations
  updateFilter: (updates: Partial<DisplayScheduleFilter>) => void;

  // Computed values
  totalEvents: number;
  hasEvents: boolean;
  dateRange: {
    startDate?: string;
    endDate?: string;
    requestedDate?: string;
    requestedStartDate?: string;
    requestedEndDate?: string;
  } | null;

  // Error handling
  lastError: string | null;
  clearError: () => void;

  // Cache info
  isFromCache: boolean;
}

export const useDisplaySchedule = (
  options: UseDisplayScheduleOptions = {},
): UseDisplayScheduleReturn => {
  const {
    filter: initialFilter = {},
    autoLoad = true,
    refreshInterval,
  } = options;

  // Get network status and cache
  const { isOffline } = useNetwork();
  const {
    //isConnected,
    onDataUpdated,
    //lastError: pubsubError,
  } = useSignalRContext();

  const cache = useCache();

  // Data state
  const [scheduleData, setScheduleData] = useState<DisplayScheduleData[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFromCache, setIsFromCache] = useState(false);

  // Filter state - much cleaner initialization
  const [filter, setFilter] = useState<DisplayScheduleFilter>(() => {
    return { ...initialFilter };
  });

  // Ref to track current filter for interval (avoids dependency cycle)
  const currentFilterRef = useRef<DisplayScheduleFilter>(filter);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Update ref when filter changes
  useEffect(() => {
    currentFilterRef.current = filter;
  }, [filter]);

  // Generate cache key from filter - dynamic and future-proof
  const getCacheKey = useCallback((filter: DisplayScheduleFilter): string => {
    // Get all defined (non-undefined) filter properties and create deterministic key
    const definedEntries = Object.entries(filter)
      .filter(([_, value]) => value !== undefined)
      .sort(([a], [b]) => a.localeCompare(b)); // Sort for consistent key generation

    const keyParts = definedEntries.map(([key, value]) => {
      if (Array.isArray(value)) {
        return `${key}:${value.join(",")}`;
      }
      return `${key}:${value}`;
    });

    return `schedule:${keyParts.join("|")}`;
  }, []);

  // Computed values
  const isEmpty = scheduleData[0]?.events.length === 0 && !loading;

  const totalEvents = useMemo(
    () => scheduleData.reduce((total, data) => total + data.totalEvents, 0),
    [scheduleData],
  );

  const hasEvents = useMemo(() => totalEvents > 0, [totalEvents]);

  const dateRange = useMemo(() => {
    if (scheduleData.length === 0) return null;
    return scheduleData[0]?.dateRange || null;
  }, [scheduleData]);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Load schedule function
  const loadSchedule = useCallback(
    async (filter: DisplayScheduleFilter) => {
      // Don't load if no filter criteria is provided
      if (!filter.displayId && !filter.venueId) {
        console.log("Don't load if no filter criteria is provided");
        setScheduleData([]);
        setIsFromCache(false);
        return;
      }

      // Set current's app version
      filter.appVersion = import.meta.env.VITE_APP_VERSION;

      setFilter(filter);
      const cacheKey = getCacheKey(filter);

      // If offline, try cache first
      if (isOffline) {
        const cachedData = cache.get<DisplayScheduleData[]>(cacheKey);
        if (cachedData) {
          console.debug("useDisplaySchedule: Using cached data (offline)");
          setScheduleData(cachedData);
          setIsFromCache(true);
          setLastError(null);
          return;
        } else {
          console.debug(
            "useDisplaySchedule: No cached data available (offline)",
          );
          setLastError("No cached data available while offline");
          setIsFromCache(false);
          return;
        }
      }

      // Online: Try API first, fallback to cache on error
      setLoading(true);
      setLastError(null);

      try {
        const result = await DisplayScheduleService.getSchedule(filter);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newScheduleData = Array.isArray(result.data)
            ? result.data.map((item: any) => {
                const obj: any = JSON.parse(JSON.stringify(item));
                if (obj.display) {
                  try {
                    obj.display.themeConfig = parseThemeConfig(
                      item.display.theme?.themeConfig,
                    );
                    obj.display.layoutConfig = parseLayoutConfig(
                      item.display.layout?.layoutConfig,
                    );
                  } catch (ex: any) {
                    console.log("=== ERROR", ex.message);
                  }
                }

                return obj;
              })
            : [];

          // Store in cache
          cache.set(cacheKey, newScheduleData);

          setScheduleData(newScheduleData);
          setIsFromCache(false);
          setLastError(null);
          console.debug("useDisplaySchedule: Loaded from API and cached");
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load schedule data";

          // Try to fallback to cache
          const cachedData = cache.get<DisplayScheduleData[]>(cacheKey);
          if (cachedData) {
            console.debug(
              "useDisplaySchedule: API error, using cached fallback",
            );
            setScheduleData(cachedData);
            setIsFromCache(true);
            setLastError(null); // Clear error since we have fallback data
          } else {
            setLastError(errorMessage);
            setIsFromCache(false);
          }
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load schedule data";

        // Try to fallback to cache
        const cachedData = cache.get<DisplayScheduleData[]>(cacheKey);
        if (cachedData) {
          console.debug(
            "useDisplaySchedule: Network error, using cached fallback",
          );
          setScheduleData(cachedData);
          setIsFromCache(true);
          setLastError(null); // Clear error since we have fallback data
        } else {
          setLastError(errorMessage);
          setIsFromCache(false);
        }
      } finally {
        setLoading(false);
      }
    },
    [isOffline, cache, getCacheKey],
  );

  // Refresh schedule (same as load)
  const refreshSchedule = useCallback(async () => {
    await loadSchedule(filter);
  }, [loadSchedule, filter]);

  // Listen for real-time updates
  useEffect(() => {
    const unsubscribe = onDataUpdated((message) => {
      console.log("Received update notification:", message);

      // The message type lives at the top level of the payload (the backend
      // publishes `{ type, timestamp, ...}` — e.g. `{ type: "reload" }` for a
      // reload command, `{ type: "dataUpdated" }` for a data change). Reading
      // `message.data.type` here was always undefined, so reload never fired.
      if (message?.type === "reload") {
        // Force onto the latest deployed version (bypass the service-worker
        // cache); a plain location.reload() would re-serve the cached bundle.
        void forceReloadToLatestVersion();
      } else {
        // Refresh your data
        refreshSchedule();
      }
    });

    // Cleanup: unsubscribe when component unmounts
    return unsubscribe;
  }, [onDataUpdated, refreshSchedule]);

  // Filter update function
  const updateFilter = useCallback(
    (updates: Partial<DisplayScheduleFilter>) => {
      setFilter((prev) => ({ ...prev, ...updates }));
    },
    [],
  );

  // Effect to load data when filter changes
  useEffect(() => {
    if (autoLoad) {
      if (refreshInterval) {
        loadSchedule({
          ...filter,
          date: getScheduleDate(),
        });
      } else {
        loadSchedule(filter);
      }
    }
  }, [autoLoad, loadSchedule]);

  // Effect for auto-refresh (only when online)
  useEffect(() => {
    if (!refreshInterval || !autoLoad || isOffline) return;

    console.log(`Auto load with refreshing with interval ${refreshInterval}ms`);

    const interval = setInterval(() => {
      loadSchedule({
        ...currentFilterRef.current,
        date: getScheduleDate(),
      });
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [refreshInterval, autoLoad, loadSchedule]);

  return {
    // Data state
    scheduleData,
    loading,
    isEmpty,

    // Filter state
    filter,

    // Data operations
    loadSchedule,
    refreshSchedule,

    // Filter operations
    updateFilter,

    // Computed values
    totalEvents,
    hasEvents,
    dateRange,

    // Error handling
    lastError,
    clearError,

    // Cache info
    isFromCache,
  };
};
